import { InputGroupField, SharingTypeEnum } from '@/types';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '../utils/helpers';

const facebookPattern = /^(https:\/\/www.facebook.com\/sharer\/sharer.php\?u=)(.*)$/i;
const twitterPattern = /^(https:\/\/twitter.com\/intent\/tweet\?text=)(.*)(&[amp;]*url=)(.*)$/i;
const emailPattern = /^(mailto:\?subject=)(.*)(&[amp;]*body=)(.*)$/i;
const googlePattern = /^(https:\/\/plus.google.com\/share\?url=)(.*)$/i;

export const getSharingType = (selector: string) => {
  const template = getTemplateIframeDocument();
  const link: HTMLElement|null = template?.querySelector(`${selector} a`);
  return link?.getAttribute('href')?.includes('private_archive_url') ? SharingTypeEnum.ONLINE_VERSION : SharingTypeEnum.CUSTOM_URL;
};

export const getSocialNetworkIconStyle = (selector: string): string => {
  const pattern = /^https:\/\/media.shopimind.(?:com|io)\/img\/icons\/social\/([0-9]{1,2})\/[0-9]{2}\/.+$/i;
  const template = getTemplateIframeDocument();
  const matches = template.querySelector(selector)?.getAttribute('src')?.match(pattern);
  return (matches && matches[1]) ?? '';
};

// V4: utilise socialv2 sans dossier de taille
export const getSocialNetworkIconStyleV4 = (selector: string): string => {
  // Pattern pour socialv2 (nouveau chemin)
  const patternV2 = /^https:\/\/media.shopimind.(?:com|io)\/img\/icons\/socialv2\/([0-9]{1,2})\/.+$/i;
  // Pattern pour social (ancien chemin) - fallback
  const patternV1 = /^https:\/\/media.shopimind.(?:com|io)\/img\/icons\/social\/([0-9]{1,2})\/[0-9]{1,2}\/.+$/i;
  const template = getTemplateIframeDocument();
  const src = template.querySelector(selector)?.getAttribute('src');

  // Essayer d'abord le pattern v2
  let matches = src?.match(patternV2);
  if (matches && matches[1]) {
    return matches[1];
  }

  // Fallback sur le pattern v1
  matches = src?.match(patternV1);
  if (matches && matches[1]) {
    return matches[1];
  }

  return '1';
};

export const getCustomUrl = (selector: string): string => {
  const template = getTemplateIframeDocument();
  // try with facebook
  const facebookLink: HTMLElement|null = template?.querySelector(`${selector} .spm_facebook a`);
  if (facebookLink) {
    const matches = facebookLink.getAttribute('href')?.match(facebookPattern);
    if (matches) {
      return matches[2];
    }
  }
  // try with twitter
  const twitterLink: HTMLElement|null = template?.querySelector(`${selector} .spm_twitter a`);
  if (twitterLink) {
    const matches = twitterLink.getAttribute('href')?.match(twitterPattern);
    if (matches) {
      return matches[4];
    }
  }
  // try with google
  const googleLink: HTMLElement|null = template?.querySelector(`${selector} .spm_google a`);
  if (googleLink) {
    const matches = googleLink.getAttribute('href')?.match(googlePattern);
    if (matches) {
      return matches[2];
    }
  }
  // try with email
  const emailLink: HTMLElement|null = template?.querySelector(`${selector} .spm_email a`);
  if (emailLink) {
    const matches = emailLink.getAttribute('href')?.match(emailPattern);
    if (matches) {
      return matches[4];
    }
  }
  return '';
};

export const getCustomDescription = (selector: string): string => {
  const template = getTemplateIframeDocument();
  // try with twitter
  const twitterLink: HTMLElement|null = template?.querySelector(`${selector} .spm_twitter a`);
  if (twitterLink) {
    const matches = twitterLink?.getAttribute('href')?.match(twitterPattern);
    if (matches) {
      return matches[2];
    }
  }
  // try with email
  const emailLink: HTMLElement|null = template?.querySelector(`${selector} .spm_email a`);
  if (emailLink) {
    const matches = emailLink?.getAttribute('href')?.match(emailPattern);
    if (matches) {
      return matches[2];
    }
  }
  return '';
};

export const getActiveSocialNetworks = (selector: string): string[] => {
  const template = getTemplateIframeDocument();
  const networks: Array<HTMLElement> = Array.from(template?.querySelectorAll(`${selector} .spm_social_icon_container`));
  return networks.reduce(
    (acc: string[], network: HTMLElement) => {
      const socialNetwork = network.getAttribute('class')?.replace('spm_social_icon_container', '').replace('spm_social_icon', '').replace(new RegExp('spm_hide', 'g'), '').trim();
      if (socialNetwork) {
        acc.push(socialNetwork);
      }

      return acc;
    },
    [],
  );
};

export const getLinkByNetwork = (selector: string, network: string): string => {
  const template = getTemplateIframeDocument();
  const networks: HTMLElement|null = template?.querySelector(selector);
  const link: HTMLAnchorElement = networks?.querySelector(`.${network} a`) as HTMLAnchorElement;
  const linkVariable = network.replace('spm_', '') === 'email' ? 'contact' : network.replace('spm_', '');
  return (link && link.getAttribute('href')) ?? `{var=social.${linkVariable}_url}`;
};

export const getSocialNetworksLinks = (selector: string) => {
  const template = getTemplateIframeDocument();
  const networks: Array<HTMLElement> = Array.from(template?.querySelectorAll(`${selector} .spm_social_icon_container`));
  return networks.reduce((acc: InputGroupField[], network) => {
    const name = network.getAttribute('class')?.replace('spm_social_icon_container', '').replace('spm_social_icon', '').replace(new RegExp('spm_hide', 'g'), '');
    if (name) {
      acc.push({
        key: name,
        label: `templateBuilder.fields.${name.replace('spm_', '').trim()}`,
        value: getLinkByNetwork(selector, name),
      });
    }
    return acc;
  },
  []);
};

export interface SocialNetworkItemV4 {
  key: string;
  network: string;
  url: string;
  customIcon?: string;
  label: string;
  // Propriétés du séparateur (padding uniquement)
  separatorPadding?: string;
}

export const getSocialNetworksItemsV4 = (selector: string): SocialNetworkItemV4[] => {
  const template = getTemplateIframeDocument();
  const networks: Array<HTMLElement> = Array.from(template?.querySelectorAll(`${selector} .spm_social_icon_container`));

  return networks.map((network) => {
    const classNames = network.getAttribute('class') || '';
    const key = classNames
      .replace('spm_social_icon_container', '')
      .replace('spm_social_icon', '')
      .replace(/spm_hide/g, '')
      .trim();

    // Détecter si c'est un séparateur
    const isSeparator = classNames.includes('spm_social_separator');
    if (isSeparator) {
      // Extraire le padding du séparateur (ou de son td enfant pour le mode vertical)
      const style = network.getAttribute('style') || '';
      const innerTd = network.querySelector('td');
      const innerStyle = innerTd?.getAttribute('style') || '';

      // Chercher padding-left/right (horizontal) ou padding-top/bottom (vertical)
      const paddingLeftMatch = style.match(/padding-left:\s*([^;]+)/) || innerStyle.match(/padding-top:\s*([^;]+)/);
      const separatorPadding = paddingLeftMatch ? paddingLeftMatch[1].trim() : '5px';

      return {
        key,
        network: 'separator',
        url: '',
        customIcon: '',
        label: 'templateBuilder.fields.separator',
        separatorPadding,
      };
    }

    const networkName = key.replace('spm_', '').split('_')[0];
    const isCustom = classNames.includes('spm_custom') || network.hasAttribute('data-custom-icon');

    const link = network.querySelector('a') as HTMLAnchorElement;
    const img = network.querySelector('img') as HTMLImageElement;

    const url = link?.getAttribute('href') || '';
    const customIcon = isCustom && img ? img.getAttribute('src') || '' : '';

    return {
      key,
      network: isCustom ? 'custom' : networkName,
      url,
      customIcon,
      label: `templateBuilder.fields.${networkName}`,
    };
  });
};
